import { jsx, css } from '@emotion/react';
import styled from '@emotion/styled';

import PriceRange from './PriceRange';
import ShingleColorsListController from './ShingleColorsListController';
import { useFeatures } from './features';
import withDefaultPrevented from '../../../withDefaultPrevented';
import Conditional from '../../Conditional';
import AlertMessage from '../../OC/oc-alert-message';
import Text from '../../OC/oc-text';
import { If, OCNameFilter, PrefixWithLocale } from '../../Utilities';
import { connectLocation } from '../../location/LocationConnectors';

const shingleLineGroup = css`
  margin-bottom: 40px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const shingleLineHeader = css`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const shingleLineName = css`
  text-decoration: none;

  .prehead {
    text-transform: capitalize;
    color: black;
    margin-bottom: 0 !important;
    font-size: 16px;
    line-height: 16px;
    font-family: 'OCRoboto';
  }

  h3 {
    margin-bottom: 0;
    line-height: 24px;

    @media (min-width: 1200px) {
      line-height: 32px;
    }
  }
`;

const featureList = css`
  width: 100%;
  margin-top: 15px;

  a:hover {
    .filter-icon {
      img {
        display: none;
      }

      .hover-icon {
        display: inline-block;
      }
    }
  }

  @media (min-width: 768px) {
    width: auto;
    margin-top: 0;
  }
`;

const filterItem = css`
  margin-right: 15px;

  &:last-child {
    margin-right: 0;
  }

  .filter-icon {
    .hover-icon {
      display: none;
    }

    &:hover {
      img {
        display: none;
      }

      .hover-icon {
        display: inline-block;
      }
    }
  }
`;

const alertSpacing = css`
  margin-bottom: 16px;
`;
const RowHeader = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: flex-end;
`;

const FeatureItem = ({ feature, onShowFeature }) => {
  const onClick = withDefaultPrevented(() => {
    if (feature.description) {
      onShowFeature(feature);
    }
  });

  return (
    <a
      href="#"
      data-track="tooltip"
      data-track-destination={`${feature.analyticsOption} Tooltip`}
      onClick={onClick}
      title={feature.name ? feature.name : feature.analyticsOption}
      css={filterItem}
    >
      <span className="filter-icon">
        <img src={feature.icon} height="20" width="20" alt={feature.alt} />
        {feature.iconHover ? <img src={feature.iconHover} height="20" width="20" className="hover-icon" /> : ''}
      </span>
    </a>
  );
};

const FeaturesList = ({ features, ...props }) => features.map(f => <FeatureItem key={f.key} feature={f} {...props} />);

const ShingleRow = connectLocation(
  ({ shingle, onShowFeature, houseView, recentlyViewed, onToggle, address, locale, t }) => {
    const features = useFeatures();
    if (!features) {
      return null;
    }

    if (shingle) {
      // In favorite page, shingle is false.
      shingle.shingle_colors = shingle.shingle_colors.sort((a, b) => (b.newcolor || 0) - (a.newcolor || 0));
    }

    const message = ['supreme', 'woodmoor', 'woodcrest', 'berkshire', 'trudefinition-duration-flex'].includes(
      shingle.uid
    )
      ? t('availability.title')
      : t('availability.limited_availability_title');
    const createAlertMessage = (title, textSize) => (
      <div css={alertSpacing}>
        <AlertMessage
          title={title}
          message={t('availability.message_w_link')}
          dismissable={false}
          type="warning"
          alertIcon
          textSize={textSize}
        />
      </div>
    );

    const discontinueBannerMessage = (message, textSize) => (
      <div css={alertSpacing}>
        <AlertMessage message={message} dismissable={false} type="black" alertIcon textSize={textSize} />
      </div>
    );

    return (
      <div css={shingleLineGroup}>
        <div css={shingleLineHeader}>
          <Conditional if={shingle}>
            <RowHeader>
              <a
                href={PrefixWithLocale(`/roofing/shingles/${shingle.uid}`, locale.code)}
                target="_blank"
                data-track="view-product"
                data-track-product-name={shingle.proper_name}
                tabIndex="0"
                css={shingleLineName}
                rel="noreferrer"
              >
                <If condition={shingle.trudefinition}>
                  <OCNameFilter className="prehead">{t('shingles.features.trudefinition.trudefinition')}</OCNameFilter>
                </If>
                <h3>
                  <OCNameFilter>{shingle.proper_name}</OCNameFilter>
                </h3>
              </a>

              <PriceRange uid={shingle.price_uid} />
            </RowHeader>

            <span css={featureList}>
              <FeaturesList
                features={features.filter(
                  feature => !feature.excludeFromFeaturesList && feature.isInShingleOrColor(shingle)
                )}
                onShowFeature={onShowFeature}
              />
            </span>
          </Conditional>
        </div>

        <Conditional if={shingle}>
          <p>
            <Text content={shingle.short_description} />
          </p>
        </Conditional>

        <Conditional if={shingle.unavailable}>{createAlertMessage(message, '13px')}</Conditional>

        <Conditional if={shingle.discontinued}>
          {discontinueBannerMessage(t('shingles.statics.discontinued'), '13px')}
        </Conditional>

        <ShingleColorsListController
          recentlyViewed={recentlyViewed}
          onToggle={onToggle}
          shingle={shingle}
          houseView={houseView}
          isPDP={false}
          address={address}
        />
      </div>
    );
  }
);

export default ShingleRow;
